import { LOCAL_STORAGE_EXISTS } from "../constants";
import { SALEOR_AUTH_PLUGIN_ID, SALEOR_CSRF_TOKEN, SALEOR_REFRESH_TOKEN } from "./constants";

export let storage: {
  setAuthPluginId: (method: string | null) => void;
  getAuthPluginId: () => string | null;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setCSRFToken: (token: string | null) => void;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getCSRFToken: () => string | null;
  getAudience: () => string | null;
  setTokens: (tokens: {
    accessToken: string | null;
    refreshToken: string | null;
    csrfToken?: string | null;
  }) => void;
  clear: () => void;
};

export const createStorage = (autologinEnabled: boolean, audience?: string): void => {
  let authPluginId: string | null = LOCAL_STORAGE_EXISTS
    ? localStorage.getItem(SALEOR_AUTH_PLUGIN_ID)
    : null;
  let accessToken: string | null = null;
  // The refresh token is held ONLY in memory for the lifetime of the tab. It is
  // never written to localStorage — the durable copy lives in the backend's
  // httpOnly `refreshToken` cookie (set by jwt_refresh_token_middleware), out of
  // reach of JavaScript/XSS. Silent re-auth after a reload uses that cookie plus
  // the persisted CSRF token below.
  let refreshToken: string | null = null;
  // One-time cleanup: purge any refresh token left in localStorage by the old
  // (pre-cookie) flow so it can no longer be stolen.
  if (LOCAL_STORAGE_EXISTS) {
    localStorage.removeItem(SALEOR_REFRESH_TOKEN);
  }
  let csrfToken: string | null =
    autologinEnabled && LOCAL_STORAGE_EXISTS ? localStorage.getItem(SALEOR_CSRF_TOKEN) : null;

  const setAuthPluginId = (pluginId: string | null): void => {
    if (LOCAL_STORAGE_EXISTS) {
      if (pluginId) {
        localStorage.setItem(SALEOR_AUTH_PLUGIN_ID, pluginId);
      } else {
        localStorage.removeItem(SALEOR_AUTH_PLUGIN_ID);
      }
    }

    authPluginId = pluginId;
  };

  const setRefreshToken = (token: string | null): void => {
    // In-memory only — intentionally not persisted (see note above).
    refreshToken = token;
  };

  const setCSRFToken = (token: string | null): void => {
    if (LOCAL_STORAGE_EXISTS) {
      if (token) {
        localStorage.setItem(SALEOR_CSRF_TOKEN, token);
      } else {
        localStorage.removeItem(SALEOR_CSRF_TOKEN);
      }
    }

    csrfToken = token;
  };

  const setAccessToken = (token: string | null): void => {
    accessToken = token;
  };

  const getAuthPluginId = (): string | null => authPluginId;
  const getAccessToken = (): string | null => accessToken;
  const getRefreshToken = (): string | null => refreshToken;
  const getCSRFToken = (): string | null => csrfToken;
  // The audience this portal authenticates as (e.g. "admin", "seller"). The
  // backend scopes the httpOnly refresh-token cookie by it (refreshToken-admin,
  // refreshToken-seller, ...) so portals sharing the API host don't overwrite
  // each other's sessions. Static per client; survives nothing — it's config.
  const getAudience = (): string | null => audience ?? null;

  const setTokens = ({
    accessToken,
    refreshToken,
    csrfToken,
  }: {
    accessToken: string | null;
    refreshToken: string | null;
    csrfToken?: string | null;
  }): void => {
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    if (csrfToken !== undefined) {
      setCSRFToken(csrfToken);
    }
  };

  const clear = (): void => {
    setAuthPluginId(null);
    setAccessToken(null);
    setRefreshToken(null);
    setCSRFToken(null);
  };

  storage = {
    setAuthPluginId,
    setAccessToken,
    setRefreshToken,
    setCSRFToken,
    getAuthPluginId,
    getAccessToken,
    getRefreshToken,
    getCSRFToken,
    getAudience,
    setTokens,
    clear,
  };
};
